/**
 * SeedDrawerContent Component
 *
 * Routes and renders tab content for the SeedDrawer based on the active tab.
 * Handles different tab types (details, history, notes, etc.) and seed-type-specific
 * content (authentication/sitemap for web applications only).
 *
 * This component acts as a content router that:
 * - Switches between different tab views based on activeTab value
 * - Conditionally renders seed-type-specific tabs (webapp authentication/sitemap)
 * - Manages loading states while data is being fetched
 * - Passes appropriate data to each tab component
 *
 * @component
 */

import { useCallback } from 'react';
import { toast } from 'sonner';

import { Loader } from '@/components/Loader';
import { Container } from '@/components/legacy-ui';
import { useUpdateSeed } from '@/hooks/useSeeds';
import { NotesTab } from '@/sections/NotesTab';
import {
  AuthenticationTab,
  DetailsTab,
  DomainHistoryTab,
  SeedHistoryTab,
  SitemapTab,
} from '@/sections/seeds/components/drawer/tabs';
import { Access, Seed } from '@/types';

interface SeedDrawerContentProps {
  /** The seed to display content for */
  seed: Seed;
  /** Currently active tab identifier */
  activeTab: string;
  /** Formatted details content (WHOIS data or IP information) */
  displayDetails?: string;
  /** Array of historical WHOIS records for domain history tab */
  history?: string[];
  /** Information about how the seed was discovered/originated */
  originationInfo: string[];
  /** Access data for resolving user emails in history timeline */
  accesss: Access[];
  /** Loading status for details data */
  detailsStatus: 'error' | 'success' | 'pending';
  /** Loading status for history data */
  historyStatus: 'error' | 'success' | 'pending';
  /** Callback to open comment modal for adding notes (omit to hide Add Note button) */
  onCommentClick?: () => void;
}

/**
 * SeedDrawerContent routes to the appropriate tab content
 *
 * Renders different tab components based on the activeTab value:
 * - 'details': Shows WHOIS/domain details and origination info
 * - 'domainHistory': Shows historical WHOIS data changes
 * - 'seedHistory': Shows seed lifecycle timeline
 * - 'authentication': Shows webapp authentication (webapplication only)
 * - 'sitemap': Shows webapp sitemap (webapplication only)
 * - 'seedNotes': Shows notes associated with the seed
 *
 * Loading states are managed globally and applied to all tabs except notes.
 *
 * @param seed - The seed object containing all metadata
 * @param activeTab - The currently selected tab identifier
 * @param displayDetails - Formatted details string for details tab
 * @param history - WHOIS history records for domain history tab
 * @param originationInfo - Discovery source information
 * @param accesss - Access objects for user email resolution
 * @param detailsStatus - Loading state for details data
 * @param historyStatus - Loading state for history data
 * @param onCommentClick - Handler for opening the comment modal
 */
export function SeedDrawerContent({
  seed,
  activeTab,
  displayDetails,
  history,
  originationInfo,
  accesss,
  detailsStatus,
  historyStatus,
  onCommentClick,
}: SeedDrawerContentProps) {
  const { mutateAsync: updateSeed } = useUpdateSeed();

  // Handler for removing notes from seeds (uses different mutation than other entity types)
  const handleRemoveNote = useCallback(
    async (index: number) => {
      await updateSeed({
        type: seed.class === 'webapplication' ? 'webapplication' : 'asset',
        key: seed.key,
        identifier: seed.identifier,
        remove: index,
        showSnackbar: false,
      });
      toast.success('Note removed successfully');
    },
    [updateSeed, seed.class, seed.key, seed.identifier]
  );

  // Render tab content based on activeTab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'authentication':
        return <AuthenticationTab seed={seed} />;

      case 'sitemap':
        return <SitemapTab seed={seed} />;

      case 'details':
        return (
          <DetailsTab
            seed={seed}
            displayDetails={displayDetails}
            originationInfo={originationInfo}
          />
        );

      case 'domainHistory':
        return <DomainHistoryTab seed={seed} history={history} />;

      case 'seedHistory':
        return <SeedHistoryTab seed={seed} accesss={accesss} />;

      case 'seedNotes':
        return (
          <NotesTab
            resource={seed}
            resourceType="Seed"
            accesss={accesss}
            onRemoveNote={onCommentClick ? handleRemoveNote : undefined}
            onOpenCommentModal={onCommentClick}
          />
        );

      default:
        return (
          <div className="text-chariot-text-secondary p-4 text-center">
            Tab not found: {activeTab}
          </div>
        );
    }
  };

  return (
    <Container className="h-full p-6" tabIndex={-1} id={activeTab}>
      <Loader
        className="m-6 w-[calc(100%-48px)]"
        isLoading={detailsStatus === 'pending' || historyStatus === 'pending'}
      >
        {renderTabContent()}
      </Loader>
    </Container>
  );
}
